// CookPreviewAssigner — the "divvy up steps" UI shown on the cook
// preview/overview screen BEFORE the lead taps START COOKING. Lets
// the lead invite teammates AND pre-assign specific steps so the
// cook starts parallel:
//
//   "Marissa, you're on the onions while I do the pasta."
//   "Paul, you set the table at the end."
//
// Once START fires, each participant's CookMode opens to their
// earliest pending/accepted assigned step (handled in CookMode's
// init). Helpers see only their assigned step's controls; lead
// sees the full cook view.
//
// Lazy session creation: a cook_sessions row only gets inserted
// once the lead actually opens the assigner / picks a helper. No
// session = no clutter for solo cooks.

import { useEffect, useMemo, useState } from "react";
import { COLOR, FONT, RADIUS, SPACE } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { useTeamCookSession } from "../lib/useTeamCookSession";
import { paletteEntry } from "../lib/participantPalette";
import InviteTeammatesSheet from "./InviteTeammatesSheet";
import AssignStepSheet from "./AssignStepSheet";

export default function CookPreviewAssigner({
  userId,
  recipe,
  family = [],
  friends = [],
  onSessionReady, // (sessionId) => void — called once the session exists
}) {
  const [sessionId, setSessionId] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [assignStepId, setAssignStepId] = useState(null);
  const [profilesById, setProfilesById] = useState(new Map());

  const team = useTeamCookSession({ userId, cookSessionId: sessionId });
  const {
    participants, assignmentsByStep,
    assignStep, inviteParticipants,
  } = team;

  // Ensure a cook_sessions row exists. Lazy: only created when
  // the lead first invites someone or opens the assigner. Falls
  // back to adopting an existing active session for this recipe.
  const ensureSession = async () => {
    if (sessionId || creatingSession || !recipe?.slug) return null;
    setCreatingSession(true);
    try {
      // Adopt if my own active session for this recipe exists.
      const twoHoursAgo = new Date(Date.now() - 2 * 3600 * 1000).toISOString();
      const { data: existing } = await supabase
        .from("cook_sessions").select("id")
        .eq("user_id", userId).eq("recipe_slug", recipe.slug)
        .eq("status", "active").gte("started_at", twoHoursAgo)
        .order("started_at", { ascending: false }).limit(1).maybeSingle();
      if (existing?.id) {
        setSessionId(existing.id);
        onSessionReady?.(existing.id);
        return existing.id;
      }
      const { data, error } = await supabase
        .from("cook_sessions")
        .insert({
          user_id:      userId,
          recipe_slug:  recipe.slug,
          recipe_title: recipe.title || null,
          recipe_emoji: recipe.emoji || null,
          status:       "active",
        })
        .select("id").single();
      if (error) { console.warn("[preview-assigner] session insert:", error.message); return null; }
      // Self as lead — participants array is empty for solo so add row.
      await supabase.from("cook_session_participants").upsert({
        cook_session_id: data.id,
        user_id:         userId,
        role:            "lead",
        color_tag:       "",
        invited_by:      userId,
        accepted_at:     new Date().toISOString(),
      }, { onConflict: "cook_session_id,user_id", ignoreDuplicates: true });
      setSessionId(data.id);
      onSessionReady?.(data.id);
      return data.id;
    } finally {
      setCreatingSession(false);
    }
  };

  // Hydrate profiles for chip labels
  useEffect(() => {
    if (!participants?.length) return;
    const ids = participants.map((p) => p.user_id);
    let alive = true;
    (async () => {
      const { data } = await supabase.from("profiles").select("id, name").in("id", ids);
      if (alive) setProfilesById(new Map((data || []).map((p) => [p.id, p])));
    })();
    return () => { alive = false; };
  }, [participants]);

  const steps = recipe?.steps || [];
  const helperParticipants = useMemo(
    () => participants.filter((p) => p.role !== "diner" && p.user_id !== userId && !p.left_at),
    [participants, userId],
  );

  const handleInviteClick = async () => {
    await ensureSession();
    setInviteOpen(true);
  };

  const stepIdToAssign = useMemo(() => {
    if (assignStepId == null) return null;
    return steps.find((s) => String(s.id) === String(assignStepId)) || null;
  }, [assignStepId, steps]);

  return (
    <div
      style={{
        marginTop: 18,
        padding: "16px 16px 12px",
        background: COLOR.deep,
        border: `1px solid ${COLOR.border}`,
        borderRadius: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: "0.14em",
            color: COLOR.sub,
            fontWeight: 700,
          }}
        >
          COOK TEAM
        </div>
        <button
          type="button"
          onClick={handleInviteClick}
          style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: "0.1em",
            padding: "5px 12px",
            background: helperParticipants.length ? COLOR.soil : COLOR.leaf,
            color: helperParticipants.length ? COLOR.ink : "#fff",
            border: helperParticipants.length ? `1px solid ${COLOR.border}` : "none",
            borderRadius: 999,
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          {helperParticipants.length ? "+ INVITE" : "+ INVITE COOKS"}
        </button>
      </div>

      {/* Participant chips */}
      {participants.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {participants.map((p) => {
            const profile = profilesById.get(p.user_id);
            const palette = paletteEntry(p.color_tag);
            const name = profile?.name || "Teammate";
            const isMe = p.user_id === userId;
            return (
              <div
                key={p.user_id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px 4px 4px",
                  background: palette.bg,
                  border: `1px solid ${palette.ring}`,
                  borderRadius: 999,
                }}
              >
                <span style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: palette.bgSolid, color: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
                }}>{name.slice(0, 1).toUpperCase()}</span>
                <span style={{
                  fontFamily: FONT.mono, fontSize: 10, letterSpacing: "0.06em",
                  color: COLOR.ink, textTransform: "uppercase", fontWeight: 700,
                }}>{isMe ? "ME" : name.split(" ")[0]}</span>
                <span style={{
                  fontFamily: FONT.mono, fontSize: 8, letterSpacing: "0.1em",
                  color: COLOR.sub,
                }}>{p.role.toUpperCase()}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Step assignment list — only meaningful when there's at least
          one helper. For solo cooks, no point showing this. */}
      {helperParticipants.length > 0 && (
        <>
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: "0.14em",
              color: COLOR.sub,
              fontWeight: 700,
              marginBottom: 8,
            }}
          >
            DIVVY UP STEPS
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 4 }}>
            {steps.map((step, idx) => {
              const assignments = assignmentsByStep.get(String(step.id)) || [];
              const live = assignments.filter((a) => a.status !== "declined" && a.status !== "done");
              return (
                <button
                  key={step.id || idx}
                  type="button"
                  onClick={() => setAssignStepId(step.id)}
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    background: live.length ? COLOR.goldDeep : "#fff",
                    border: `1px solid ${live.length ? COLOR.goldDim : COLOR.border}`,
                    borderRadius: 10,
                    touchAction: "manipulation",
                  }}
                >
                  <span style={{
                    fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
                    color: COLOR.sub, minWidth: 24,
                  }}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span style={{
                    flex: 1,
                    fontFamily: FONT.detail, fontStyle: "italic",
                    fontSize: 14, color: COLOR.ink,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {step.title || `Step ${idx + 1}`}
                  </span>
                  {live.length > 0 ? (
                    <div style={{ display: "flex", gap: 3 }}>
                      {live.slice(0, 3).map((a) => {
                        const profile = profilesById.get(a.assigned_to);
                        const palette = paletteEntry(
                          participants.find((p) => p.user_id === a.assigned_to)?.color_tag,
                        );
                        return (
                          <span key={a.id} title={profile?.name || "Teammate"} style={{
                            width: 22, height: 22, borderRadius: "50%",
                            background: palette.bgSolid, color: "#fff",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
                          }}>
                            {(profile?.name || "T").slice(0, 1).toUpperCase()}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <span style={{
                      fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.1em",
                      color: COLOR.sub, fontWeight: 600,
                    }}>
                      ASSIGN →
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      <InviteTeammatesSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        family={family}
        friends={friends}
        selfUserId={userId}
        existingIds={new Set(participants.map((p) => p.user_id))}
        onInvite={async (picks) => {
          await ensureSession();
          await inviteParticipants(picks);
        }}
      />

      <AssignStepSheet
        open={assignStepId != null}
        onClose={() => setAssignStepId(null)}
        step={stepIdToAssign}
        participants={participants}
        existingAssignments={
          stepIdToAssign ? (assignmentsByStep.get(String(stepIdToAssign.id)) || []) : []
        }
        profilesById={profilesById}
        selfUserId={userId}
        onAssign={async ({ assigneeIds, deadline }) => {
          await ensureSession();
          await assignStep({
            stepId:     stepIdToAssign.id,
            stepTitle:  stepIdToAssign.title,
            assigneeIds,
            deadline,
          });
        }}
      />
    </div>
  );
}
